import { isBrowser } from '~/utilities/helpers';

const TOKEN_KEYS = {
    ACCESS: 'authAccessToken',
    REFRESH: 'authRefreshToken'
};

const TOKEN_EXPIRY_BUFFER_SECONDS = 60;

export const decodeJwt = (token) => {
    if (!token) return null;
    try {
        const payload = token.split('.')[1];
        return JSON.parse(atob(payload));
    } catch {
        return null;
    }
};

export const getJwtExpiry = (token) => {
    const payload = decodeJwt(token);
    return payload?.exp ? payload.exp * 1000 : null;
};

export const isJwtExpired = (token, bufferSeconds = 0) => {
    const expiry = getJwtExpiry(token);
    if (!expiry) return true;
    return Date.now() >= expiry - bufferSeconds * 1000;
};

export const saveTokens = ({ access, refresh }) => {
    if (isBrowser()) {
        if (access) localStorage.setItem(TOKEN_KEYS.ACCESS, access);
        if (refresh) localStorage.setItem(TOKEN_KEYS.REFRESH, refresh);
    }
};

export const saveAccessToken = (accessToken) => {
    if (isBrowser() && accessToken) {
        localStorage.setItem(TOKEN_KEYS.ACCESS, accessToken);
    }
};

export const getAccessToken = () => {
    if (isBrowser()) {
        return localStorage.getItem(TOKEN_KEYS.ACCESS);
    }
    return null;
};

export const getRefreshToken = () => {
    if (isBrowser()) {
        return localStorage.getItem(TOKEN_KEYS.REFRESH);
    }
    return null;
};

export const isAccessTokenExpired = () => {
    const token = getAccessToken();
    return isJwtExpired(token, TOKEN_EXPIRY_BUFFER_SECONDS);
};

export const isRefreshTokenExpired = () => {
    const token = getRefreshToken();
    return isJwtExpired(token);
};

export const resetTokens = () => {
    if (isBrowser()) {
        localStorage.removeItem(TOKEN_KEYS.ACCESS);
        localStorage.removeItem(TOKEN_KEYS.REFRESH);
    }
};

export const isLoggedIn = () => {
    if (isBrowser()) {
        const accessToken = getAccessToken();
        if (accessToken && !isJwtExpired(accessToken)) {
            return true;
        }
        const refreshToken = getRefreshToken();
        if (refreshToken && !isJwtExpired(refreshToken)) {
            return true;
        }
    }
    return false;
};

export const setDefaultVehicleId = (vehicleId) => {
    if (isBrowser() && vehicleId) {
        localStorage.setItem('defaultVehicleId', vehicleId);
    }
};

export const getDefaultVehicleId = () => {
    if (isBrowser()) {
        return localStorage.getItem('defaultVehicleId');
    }
    return null;
};

export const unknownErrorMessage = 'An unknown error has occurred.';

export const errorMessageOverrides = {
    USERNAME_EXISTS:
        'There is already an account associated with that email address.',
    INVALID_PARAMETER: 'Please ensure all fields are valid.',
    LIMIT_EXCEEDED: 'Number of forgot password attempts has been exceeded.',
    NOT_AUTHORIZED: 'Not authorized to perform this function.',
    INVALID_PASSWORD: 'Please enter a valid password.',
    CODE_MISMATCH: 'The provided confirmation code does not match.',
    EXPIRED_CODE:
        'The provided confirmation code has expired. Please request a new one.',
    TOO_MANY_FAILED_ATTEMPTS:
        'Too many failed attempts, please try again later.',
    FAILURE: 'An error has occurred processing your submission.',
    CANNOT_FIND_VEHICLE: 'Vehicle not found.',
    VEHICLE_SUBSCRIPTION_NOT_FOUND:
        'This vehicle does not have a current subscription. You will need to create a new subscription.',
    USER_ALREADY_ON_ACCOUNT:
        'This user is already associated with this garage.',
    USER_NOT_ON_ACCOUNT: 'This user is not associated with this garage.',
    ARCHIVED: 'This user account has been archived.',
    COMPROMISED: 'This user account has been compromised.',
    AMP_Maintenance:
        "We're currently undergoing maintenance. Please try again later.",
    UNKNOWN: unknownErrorMessage
};

export const getErrorMessage = (status) => {
    if (status === 'SUCCESS') {
        return null;
    } else if (errorMessageOverrides.hasOwnProperty(status)) {
        return errorMessageOverrides[status];
    } else {
        return unknownErrorMessage;
    }
};

export const emailRegex =
    /^[_a-z0-9-]+(\.[_a-z0-9-]+)*(\+[a-z0-9-]+)?@[a-z0-9-]+(\.[a-z0-9-]+)*$/i;

// Exact error string emitted by the Django backend when a migrated AMP user
// tries to sign in before completing the mobile-app password setup flow.
// Source: autobell-backend sign-in serializer; raised via
// MigrationPasswordService when `migration_password_setup_required` is true.
// If the backend message changes, update this constant.
export const MIGRATION_PASSWORD_REQUIRED_MESSAGE =
    'Migrated user must set up a password.';
